import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Input,
  toast,
} from '@startupquickstart/frontend';

import { ARTIFACT_EFFECT_META } from '@/components/artifacts/demo/artifact-effect-meta';
import type { ArtifactCategory } from '@/config/objects/artifacts';
import {
  useDismissEffect,
  useTeamEffects,
  useUpdateEffect,
  type ActiveEffect,
} from '@/hooks/useEventEffects';

const CARD_CLASSES = 'rounded-xl border bg-card shadow-sm py-0 gap-0';
const CARD_HEADER_CLASSES = 'px-6 py-4 !pb-4 border-b';

/** Light-mode category chip styling for the admin portal. */
const CATEGORY_CHIP: Record<ArtifactCategory, string> = {
  offensive: 'bg-red-100 text-red-700',
  defensive: 'bg-blue-100 text-blue-700',
  wildcard: 'bg-purple-100 text-purple-700',
};

/** Formats an ISO expiry as `Ends in Mm Ss`, or the until-used case. */
function formatRemaining(expiresAt: string | null): string {
  if (!expiresAt) return 'Until used';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expiring…';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s left` : `${s}s left`;
}

/** Live-ticking remaining-time label. */
function RemainingTime({ expiresAt }: { expiresAt: string | null }) {
  const [label, setLabel] = useState(() => formatRemaining(expiresAt));
  useEffect(() => {
    setLabel(formatRemaining(expiresAt));
    if (!expiresAt) return;
    const id = setInterval(() => setLabel(formatRemaining(expiresAt)), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return <span className="font-mono text-sm tabular-nums text-muted-foreground">{label}</span>;
}

function EffectRow({
  effect,
  eventId,
  onDismiss,
}: {
  effect: ActiveEffect;
  eventId: string;
  onDismiss: (effect: ActiveEffect) => void;
}) {
  const artifact = effect.teamArtifact.artifact;
  const meta = ARTIFACT_EFFECT_META[artifact.artifactType];
  const Icon = meta?.icon;
  const updateMutation = useUpdateEffect(eventId);

  const [editing, setEditing] = useState(false);
  const [minutes, setMinutes] = useState('');

  const handleSet = () => {
    const value = Number.parseInt(minutes, 10);
    if (!Number.isFinite(value) || value < 1) {
      toast.error('Enter a duration of at least 1 minute');
      return;
    }
    updateMutation.mutate(
      { effectId: effect.id, durationMinutes: value },
      {
        onSuccess: () => {
          toast.success(`${artifact.name} set to ${value} min`);
          setEditing(false);
          setMinutes('');
        },
        onError: (err: Error) => toast.error(err?.message || 'Failed to update effect'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
          {Icon && <Icon className="h-4 w-4 text-foreground" />}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-foreground">{artifact.name}</p>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_CHIP[artifact.artifactCategory]}`}
            >
              {artifact.artifactCategory}
            </span>
          </div>
          <p className="truncate text-xs text-muted-foreground">from {effect.sourceTeam.name}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:justify-end">
        <RemainingTime expiresAt={effect.expiresAt} />
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="min"
              className="h-8 w-20"
              aria-label={`New duration for ${artifact.name}`}
            />
            <Button size="sm" onClick={handleSet} loading={updateMutation.isPending}>
              Set
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Edit time
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDismiss(effect)}>
              Dismiss
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Facilitator control on the team page: lists the team's active artifact
 * effects with a live countdown, and lets a facilitator retime (Edit time
 * → new minutes) or dismiss each one. Renders nothing when the team has no
 * active effects. Mutations broadcast to the team's device + Live view.
 */
export function TeamActiveEffectsCard({ eventId, teamId }: { eventId: string; teamId: string }) {
  const { data } = useTeamEffects(eventId, teamId);
  const dismissMutation = useDismissEffect(eventId);
  const [pendingDismiss, setPendingDismiss] = useState<ActiveEffect | null>(null);

  const effects = data?.data ?? [];
  if (effects.length === 0) return null;

  const confirmDismiss = () => {
    if (!pendingDismiss) return;
    const effect = pendingDismiss;
    dismissMutation.mutate(
      { effectId: effect.id },
      {
        onSuccess: () => {
          toast.success(`${effect.teamArtifact.artifact.name} dismissed`);
          setPendingDismiss(null);
        },
        onError: (err: Error) => toast.error(err?.message || 'Failed to dismiss effect'),
      },
    );
  };

  return (
    <>
      <Card className={CARD_CLASSES}>
        <CardHeader className={CARD_HEADER_CLASSES}>
          <CardTitle>Active Effects</CardTitle>
          <CardDescription>Artifact effects currently applied to this team</CardDescription>
        </CardHeader>
        <CardContent className="divide-y px-6 py-2">
          {effects.map((effect) => (
            <EffectRow
              key={effect.id}
              effect={effect}
              eventId={eventId}
              onDismiss={setPendingDismiss}
            />
          ))}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!pendingDismiss}
        onOpenChange={(open) => !open && setPendingDismiss(null)}
        title="Dismiss effect?"
        description={
          pendingDismiss
            ? `This clears ${pendingDismiss.teamArtifact.artifact.name} from the team immediately.`
            : ''
        }
        confirmText="Dismiss"
        variant="destructive"
        loading={dismissMutation.isPending}
        onConfirm={confirmDismiss}
      />
    </>
  );
}
